import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import dashboardService from "@/services/api/dashboard";
import type {
  DashboardSummary,
  GetDashboardSummaryParams,
  GetUpcomingEventsParams,
  UpcomingEvent,
} from "@/services/api/dashboard";

export interface DashboardState {
  summary: DashboardSummary | null;
  isFetching: boolean;
  error: string | null;
  upcomingEvents: UpcomingEvent[];
  totalUpcomingEvents: number;
  isFetchingUpcomingEvents: boolean;
  upcomingEventsError: string | null;
}

const initialState: DashboardState = {
  summary: null,
  isFetching: false,
  error: null,
  upcomingEvents: [],
  totalUpcomingEvents: 0,
  isFetchingUpcomingEvents: false,
  upcomingEventsError: null,
};

export const getDashboardSummary = createAsyncThunk(
  "dashboard/getDashboardSummary",
  async (params: GetDashboardSummaryParams) => dashboardService.getDashboardSummary(params),
);

export const getUpcomingEvents = createAsyncThunk(
  "dashboard/getUpcomingEvents",
  async (params: GetUpcomingEventsParams | undefined) => dashboardService.getUpcomingEvents(params),
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardSummary.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(getDashboardSummary.fulfilled, (state, action) => {
        state.isFetching = false;
        state.summary = action.payload?.data ?? null;
      })
      .addCase(getDashboardSummary.rejected, (state, action) => {
        state.isFetching = false;
        state.summary = null;
        state.error = action.error.message ?? "Failed to load dashboard summary";
      })
      .addCase(getUpcomingEvents.pending, (state) => {
        state.isFetchingUpcomingEvents = true;
        state.upcomingEventsError = null;
      })
      .addCase(getUpcomingEvents.fulfilled, (state, action) => {
        state.isFetchingUpcomingEvents = false;
        state.upcomingEvents = action.payload?.data?.results ?? [];
        state.totalUpcomingEvents = action.payload?.data?.totalRecord ?? state.upcomingEvents.length;
      })
      .addCase(getUpcomingEvents.rejected, (state, action) => {
        state.isFetchingUpcomingEvents = false;
        state.upcomingEvents = [];
        state.upcomingEventsError = action.error.message ?? "Failed to load upcoming gatherings";
      });
  },
});

export default dashboardSlice.reducer;
